import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, CheckCircle2, AlertCircle, Droplets } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const esBienvenida = params.get('bienvenida') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setOk(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'El enlace no es válido o ha caducado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#030303' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/manklar-logo-white.png" alt="Manklar" className="h-14 mx-auto mb-4" />
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10">
            <Droplets className="w-4 h-4 text-sky-400" />
            <span className="text-xs text-zinc-300">{esBienvenida ? 'Activa tu Portal Cliente' : 'Restablecer contraseña'}</span>
          </div>
        </div>

        <div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(10,10,10,0.8)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="reset-password-card">
          {!token ? (
            <p className="text-sm text-red-400 text-center">Enlace inválido: falta el token.</p>
          ) : ok ? (
            <div className="text-center space-y-4" data-testid="reset-password-ok">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <h2 className="text-xl font-light text-white font-['Outfit']">
                {esBienvenida ? '¡Cuenta activada!' : 'Contraseña actualizada'}
              </h2>
              <p className="text-sm text-zinc-400">
                Ya puedes acceder con tu correo <b className="text-white">{ok.email}</b> y tu nueva contraseña,
                o directamente con <b className="text-white">Iniciar sesión con Google</b> usando ese mismo correo.
              </p>
              <Link to="/login">
                <Button className="w-full h-11 mt-2 bg-white text-black hover:bg-zinc-200 rounded-xl" data-testid="reset-ir-login-btn">
                  Ir a iniciar sesión
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-light text-white font-['Outfit'] mb-2 text-center">
                {esBienvenida ? 'Crea tu contraseña' : 'Nueva contraseña'}
              </h2>
              <p className="text-xs text-zinc-500 text-center mb-6">
                {esBienvenida
                  ? 'Bienvenido a Manklar. Crea tu contraseña para activar el acceso a tu Portal Cliente.'
                  : 'Introduce tu nueva contraseña (mínimo 8 caracteres).'}
              </p>
              {error && (
                <div className="mb-4 p-3 rounded-xl flex items-center gap-2 bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nueva contraseña" required
                    className="pl-11 h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 rounded-xl"
                    data-testid="reset-password-input" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repite la contraseña" required
                    className="pl-11 h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 rounded-xl"
                    data-testid="reset-password-confirm-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-11 bg-white text-black hover:bg-zinc-200 rounded-xl font-semibold"
                  data-testid="reset-password-submit-btn">
                  {loading ? 'Guardando...' : (esBienvenida ? 'Activar mi cuenta' : 'Guardar contraseña')}
                </Button>
              </form>
            </>
          )}
        </div>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-xs text-zinc-600 hover:text-zinc-400">← Volver al inicio de sesión</Link>
        </div>
      </div>
    </div>
  );
}
